/**
 * OrderDetailPage — one order, `/orders/:orderNo`: the tracked object of
 * order to invoice (docs/features/order-to-invoice/
 * 28_order_page_one_card_implementation_summary.md; the journeys branch's
 * round 16e is the visual spec).
 *
 * No tabs (Ben, 3 Oct): one object card. The header (status and health
 * pills, customer and value, key facts: lines, needed by, customer PO,
 * project) with "Details ▾" after the facts, which opens the order's
 * details under them; then the action box (doc 27: folded to one summary
 * line, the rows behind it); then the chain — the lifecycle rail. The box is
 * what needs a person; the chain is where things are. Activity is in the
 * header's ⋯, a side sheet.
 *
 * Each rail step's ▾ opens its panel under the rail, one at a time, in the
 * rail's own rows: Quote (from one: it links to the quote) · Order (the
 * lines) · Payment · Production · Delivery today; there is no Checks step
 * (doc 38 §2.4). A planned step's panel says it is not built yet (previews
 * only). The header carries the customer's PO ("PO to follow" while owed)
 * and orders' approve-by sentence, amber when close and red once passed
 * (doc 47 §3). All of it
 * is in the query — `?step=delivery&line=.01&version=A`, `details=1`,
 * `activity=1`, `now=1` (the box open) — so a box row's thing, a deep link
 * and Back land there; the old tab addresses redirect to it.
 *
 * Row 3 (doc 57 §2): the Release step — each line at the gate, the Release
 * dialog (checks, the tick, Place now or Hold to place together, *Hold…*,
 * *Change supplier…* beside the supplier), "Release all ready lines", *Place
 * now?* on a held line, and a released line whose purchase order line was
 * cancelled (*Change supplier…* or *Cancel line*). *Change supplier…* opened
 * from the Release dialog returns to it.
 *
 * 3c (doc 47 §3): each line's *Cancel line* (or orders' reason it can't be)
 * and, with two or more versions, *Name versions*; a cancelled line struck
 * through with when and why; the ⋯'s *Order again*, opening a basket; the
 * Payment step's refund still owed and a later addition's own pay link.
 *
 * Kits (V1 completion R4): an order with kit lines has a Kits step after Order
 * (the kit lines a page at a time, a line's drops, the kit manifest), and
 * *Add kits…* in the header, an object-contract command that runs in the
 * command shell (its Contents and Drops tables are the `add_kits` widget),
 * as does a drop's *Record despatched…* and (R5) an in-house kit's *Despatch
 * together…*, started from the drops ticked on the Kits tab.
 *
 * Every write posts to the URL the order's view carries for it; a step the
 * view offers no URL for is not drawn. Every label and sentence is the
 * view's (docs/features/order-to-invoice/16_frontend_followup_spec.md §2).
 */
import { useCallback, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { ChevronDown, MoreHorizontal, Plus } from "lucide-react";
import type { Command } from "@shared-core/domain/object-ui/contract";
import { toast } from "sonner";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { isFeatureEnabled } from "@shared-core/config/features";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  DeliveryLineView,
  HeldLine,
  OrderCommand,
  OrderView,
  PresentedCommand,
} from "@shared-core/data/order-to-invoice-types";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { recordCommand } from "@shared-core/data/order-to-invoice-actions";
import { type ArtworkCommand } from "@shared-core/data/artwork-staff-types";
import type { ArtworkRowButton } from "@shared-core/domain/artwork-staff";
import type { RailStep } from "@shared-core/domain/lifecycle-rail";
import { poFormatHint } from "@shared-core/domain/credit-control-presentation";
import {
  ORDER_RAIL_STEPS,
  linesReadyToRelease,
  railFromOrder,
  withPlannedRailSteps,
  type OrderRailStep,
} from "@shared-core/domain/order-lifecycle-rail";
import {
  londonTodayIso,
  orderStepFailure,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import {
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import {
  ORDER_TAB_REDIRECTS,
  orderViewQuery,
  paths,
  type OrderPageView,
} from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../app/components/primitives/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../app/components/primitives/sheet";
import { cn } from "../app/components/primitives/utils";
import { LifecycleRail } from "../app/components/patterns/LifecycleRail";
import { ObjectHeader } from "../app/components/patterns/ObjectHeader";
import {
  CommandShell,
  endpointOf,
} from "../app/components/patterns/command-shell/CommandShell";
import { COMMAND_WIDGETS } from "../app/object-ui-registry";
import { KitsStepPanel } from "../app/components/domain/orders/KitsStepPanel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../app/components/primitives/tooltip";
import {
  DeliveryStepPanel,
  PaymentStepPanel,
  ProductionStepPanel,
  ReleaseStepPanel,
} from "../app/components/domain/orders/ChainPanels";
import { ChangeDeliveryDialog } from "../app/components/domain/orders/ChangeDeliveryDialog";
import { ChangeSupplierDialog } from "../app/components/domain/orders/ChangeSupplierDialog";
import { ReleaseDialog } from "../app/components/domain/orders/ReleaseDialog";
import { LineCompletionDialog } from "../app/components/domain/orders/DeliveryLinesTable";
import {
  despatchedTogetherToast,
  RecordDespatchedTogetherDialog,
} from "../app/components/domain/orders/RecordDespatchedTogetherDialog";
import { RecordMadeDespatchedDialog } from "../app/components/domain/orders/RecordMadeDespatchedDialog";
import { AgreeDeliveryPriceDialog } from "../app/components/domain/orders/AgreeDeliveryPriceDialog";
import { InvoiceStepPanel } from "../app/components/domain/orders/InvoiceStepPanel";
import { ArtworkAndProofs } from "../app/components/domain/artwork/ArtworkAndProofs";
import {
  ArtworkDialogHost,
  type ArtworkDialogRequest,
} from "../app/components/domain/artwork/ArtworkDialogs";
import { OrderDetailsPanel } from "../app/components/domain/orders/DetailsPanels";
import { OrderActionBox } from "../app/components/domain/orders/OrderActionBox";
import { NameVersionsDialog } from "../app/components/domain/orders/NameVersionsDialog";
import {
  AddExtraDialog,
  AddToProjectDialog,
  CancelLineDialog,
  CancelRestDialog,
  ChangePaymentMethodDialog,
  ContactCustomerDialog,
  ContactDialog,
  RecordPaymentDialog,
  RecordPoNumberDialog,
  RecordTharsternJobDialog,
  SetContactDialog,
  SetVatDialog,
} from "../app/components/domain/orders/OrderDialogs";
import {
  ActivityTimeline,
  HealthPill,
  LinesStepPanel,
  OrderStatusPill,
  type StepHref,
} from "../app/components/domain/orders/OrderPanels";
import { PlannedOrderRowGroups } from "../app/components/domain/orders/PlannedOrderSections";
import { ProjectChip } from "../app/components/domain/orders/ProjectChip";
import { AlertChips } from "../app/components/domain/needs-attention/AlertChips";
import {
  SubjectAlerts,
  useAlertSheet,
} from "../app/components/domain/needs-attention/SubjectAlerts";
import { useSubjectAlerts } from "@shared-core/domain/useSubjectAlerts";

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "not_found" }
  | { kind: "loaded"; order: OrderView };

type OpenDialog =
  | { kind: "add_extra" }
  | { kind: "add_to_project" }
  | { kind: "contact"; supplierId: string }
  | { kind: "contact_customer" }
  | { kind: "set_contact" }
  | { kind: "change_payment_method" }
  | { kind: "record_po_number" }
  | { kind: "set_vat"; line: HeldLine }
  | { kind: "record_payment"; proformaNo: string }
  | { kind: "cancel_line"; lineRef: string }
  | { kind: "name_versions"; lineRef: string }
  | { kind: "record_tharstern_job"; lineRef: string }
  | { kind: "record_despatched"; groupId: string }
  | { kind: "agree_delivery_price"; groupId: string }
  | { kind: "release"; lineRefs: string[] }
  /** `returnToRelease`: opened from the Release dialog, which reopens after. */
  | { kind: "change_supplier"; lineRef: string; returnToRelease: boolean }
  /** *Change delivery…* (doc 106 §9.4) on an item line. */
  | { kind: "change_delivery"; lineRef: string };

/** An object-contract command the page runs in the command shell (V1 completion R4: *Add kits…* and a drop's *Record despatched…*). */
interface RunningCommand {
  command: PresentedCommand;
  /** "Order ORD-K61204 · Monthly window kit". */
  context: string;
  initialBody?: Record<string, unknown>;
  /** The Kits step opens once it is done. */
  opensKits?: boolean;
}

const ADD_KITS = "orders.add_kits_to_order";
const CANCEL_ORDER = "orders.cancel_order";
const RECORD_MADE_DESPATCHED = "orders.record_made_line_despatched";
const DESPATCH_KIT_DROPS = "orders.record_kit_drops_despatched";

/** The approve-by sentence's colour: amber within two working days, red once passed (orders' tier). */
const APPROVE_BY_TONE = {
  tight: "text-amber-700 dark:text-amber-400",
  passed: "text-red-700 dark:text-red-400",
} as const;

function findDeliveryLine(
  order: OrderView,
  id: string,
): DeliveryLineView | null {
  for (const line of order.lines) {
    const found = line.delivery_lines.find((d) => d.id === id);
    if (found) return found;
  }
  return null;
}

/** The dialog an action-box row's artwork button opens (doc 66 §2.2). */
function artworkRequestOf(button: ArtworkRowButton): ArtworkDialogRequest {
  switch (button.kind) {
    case "upload_artwork":
      return { kind: "upload", versionId: button.version_id, replacing: false };
    case "decide_finding":
      return { kind: "check_warnings", versionId: button.version_id };
    case "accept_without_proof":
      return { kind: "accept", versionId: button.version_id };
    case "decide_proof":
      return { kind: "decide_proof", versionId: button.version_id };
  }
}

const isRailStep = (id: string | null): id is OrderRailStep =>
  id !== null && (ORDER_RAIL_STEPS as readonly string[]).includes(id);

export function OrderDetailPage() {
  const { orderNo: rawNo, tab: rawTab } = useParams<{
    orderNo: string;
    tab?: string;
  }>();
  const orderNo = rawNo ? decodeURIComponent(rawNo) : "";
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const capabilities = usePlatformCapabilities();
  // The page's view, all in the query: the open chain step and the line (and
  // version) it is opened at, Details, Activity, the box opened.
  const [searchParams, setSearchParams] = useSearchParams();
  const openStep = searchParams.get("step");
  const focusLineNo = searchParams.get("line");
  const focusVersion = searchParams.get("version");
  const isDetailsOpen = searchParams.get("details") === "1";
  const isActivityOpen = searchParams.get("activity") === "1";
  const isBoxOpen = searchParams.get("now") === "1";
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Needs attention on this order, its lines and its purchase orders (v1 completion I §10).
  const subjectAlerts = useSubjectAlerts({ orderNo });
  const alertSheet = useAlertSheet();
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState<DeliveryLineView | null>(null);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  // The Artwork step's dialogs: held here so the action box's rows open the same ones.
  const [artworkDialog, setArtworkDialog] =
    useState<ArtworkDialogRequest | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const objectIndex = useObjectManifest();
  const [running, setRunning] = useState<RunningCommand | null>(null);
  // A line's object-contract commands (confirm date, re-promise) run in the same command shell.
  const runLineCommand = useCallback(
    (command: Command, lineLabel: string) =>
      setRunning({ command, context: `Order ${orderNo} · ${lineLabel}` }),
    [orderNo],
  );

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    adapter
      .getOrder(orderNo)
      .then((order) => {
        if (cancelled) return;
        setState(order ? { kind: "loaded", order } : { kind: "not_found" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: "failed",
          message:
            err instanceof Error ? err.message : "Could not load the order.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, orderNo]);

  // After a command of the object contract is done: read the order again, quietly (the page stays where it is).
  const rereadOrder = useCallback(() => {
    adapter
      .getOrder(orderNo)
      .then((order) => {
        if (order) setState({ kind: "loaded", order });
      })
      .catch((err: unknown) => {
        setNotice(
          err instanceof Error
            ? err.message
            : "Could not read the order again.",
        );
      });
  }, [adapter, orderNo]);

  // The kit manifest (drops × contents): fetched with the caller's auth (never a plain anchor) and saved.
  const downloadKitManifest = useCallback(
    async (manifestUrl: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } =
          await adapter.downloadKitManifest(manifestUrl);
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  // Every step: post to the order's URL for it, show the re-read order, or
  // hand the refusal back to the dialog to show beside its fields.
  const apply = useCallback(
    async (
      order: OrderView,
      command: OrderCommand,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyOrderCommand(order, command);
        setState({ kind: "loaded", order: next });
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  // *Place now?*: the held line's purchase order is asked to be placed, with every line already on it.
  // A release switch on the order (V1 completion O): post to the URL the order's switch carries, show the
  // re-read order, or say why not.
  const setReleaseSwitch = useCallback(
    async (
      order: OrderView,
      command: Extract<
        OrderCommand,
        { kind: "set_review_before_release" | "set_deliver_together" }
      >,
    ) => {
      setNotice(null);
      const refused = await apply(order, command);
      if (refused) setNotice(refused.summary);
    },
    [apply],
  );

  const placeNow = useCallback(
    async (order: OrderView, lineRef: string) => {
      setNotice(null);
      const refused = await apply(order, {
        kind: "place_now",
        lineRef,
        input: {},
      });
      const lineNo = order.lines.find((l) => l.line_ref === lineRef)?.line_no;
      setNotice(refused ? refused.summary : `Asked to place ${lineNo} now.`);
    },
    [apply],
  );

  // A step on the Artwork step: post to the URL the order's artwork read
  // carries for it, show the re-read order, or hand the refusal back to the
  // dialog.
  const applyArtwork = useCallback(
    async (
      order: OrderView,
      command: ArtworkCommand,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyArtworkCommand(order, command);
        setState({ kind: "loaded", order: next });
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  // A file or a proof PDF, fetched with the caller's auth (never a plain anchor) and saved.
  const downloadArtworkFile = useCallback(
    async (url: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } = await adapter.downloadArtworkFamilyFile(url);
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  // Copy pay link: Make pay link for the order (or a later addition's
  // own) and copy its one link, the Stripe page to send the customer; it
  // returns them to this order's page.
  const copyPayLink = useCallback(
    async (order: OrderView, addedLineRef?: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const { links } = await adapter.startOrderCardPayment(
          order,
          addedLineRef ? { added_line_ref: addedLineRef } : {},
        );
        const payLink = links[0];
        if (!payLink) throw new Error("Make pay link answered no pay link.");
        await capabilities.copyToClipboard(payLink.href);
        setNotice("Pay link copied: send it to the customer.");
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  // Mark the excess refunded: finance says the overpayment was refunded in Xero (no dialog: it records who and when).
  const markExcessRefunded = useCallback(
    async (order: OrderView, proformaNo: string) => {
      setNotice(null);
      const refused = await apply(order, {
        kind: "mark_excess_refunded",
        proformaNo,
        input: {},
      });
      setNotice(
        refused
          ? refused.summary
          : `Marked: the excess on ${proformaNo} is refunded.`,
      );
    },
    [apply],
  );

  // Resend: the order's one email to its customer again, the pro-forma listed as owed with a fresh link.
  const resendEmail = useCallback(
    async (order: OrderView, proformaNo: string) => {
      setNotice(null);
      const refused = await apply(order, {
        kind: "resend_order_email",
        proformaNo,
        input: {},
      });
      setNotice(
        refused
          ? refused.summary
          : `Resent the order's email: ${proformaNo} is listed as owed, with a fresh link.`,
      );
    },
    [apply],
  );

  // View PDF: the sales ledger's copy of an invoice or a pro-forma, fetched
  // with the caller's auth (never a plain anchor) and saved.
  const viewDocumentPdf = useCallback(
    async (salesDocument: { pdf_url: string }) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } = await adapter.downloadSalesDocumentPdf(
          salesDocument.pdf_url,
        );
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  // A file at a URL the order's read carried (a line's print-ready file from
  // the Release dialog, a supplier-quoted line's quote document): fetched with
  // the caller's auth (never a plain anchor) and saved, as the PDFs above.
  const downloadFileAt = useCallback(
    async (file: { download_url: string }) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } = await adapter.downloadArtworkFamilyFile(
          file.download_url,
        );
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  // An old tab address (/now, /details, /activity, /lines, /delivery, and
  // the planned /artwork and /billing) opens the state that replaced it;
  // other query is kept.
  const movedTo = rawTab ? ORDER_TAB_REDIRECTS[rawTab] : undefined;
  if (movedTo) {
    const query = new URLSearchParams(searchParams);
    for (const [key, value] of orderViewQuery(movedTo)) query.set(key, value);
    return (
      <Navigate
        to={`${paths.orderDetail({ orderNo })}?${query.toString()}`}
        replace
      />
    );
  }

  if (state.kind === "loading") {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Loading order…</div>
      </div>
    );
  }

  if (state.kind === "failed") {
    return (
      <Alert variant="destructive" role="alert">
        <AlertDescription>
          <p>
            Could not load order {orderNo}: {state.message}
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  if (state.kind === "not_found" || rawTab) {
    return (
      <Card className="p-12 text-center">
        <h1 className="mb-2 text-lg">
          {rawTab ? "Page not found" : "Order not found"}
        </h1>
        <p className="text-muted-foreground">
          {rawTab ? (
            <>
              Order {orderNo} has no “{rawTab}” page.{" "}
              <Link to={paths.orderDetail({ orderNo })} className="underline">
                Open the order
              </Link>
              .
            </>
          ) : (
            <>
              There is no order {orderNo} that you can see. A Tharstern job
              number opens under{" "}
              <Link to={paths.jobs()} className="underline">
                Jobs
              </Link>
              .
            </>
          )}
        </p>
      </Card>
    );
  }

  const { order } = state;
  const lines = order.lines;
  const customerName = order.customer_name ?? order.customer_code;
  const rail = withPlannedRailSteps(railFromOrder(order));
  const hasPreviews = isFeatureEnabled("previews");
  const stepHref: StepHref = (step, lineNo) =>
    paths.orderDetail({ orderNo: order.order_no, step, line: lineNo });
  // Opening a step or Activity is a history entry, so Back closes it;
  // Details and the box replace theirs. Other query is kept.
  const setView = (
    patch: Partial<Record<keyof OrderPageView, string | null>>,
    { replace = false }: { replace?: boolean } = {},
  ) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace },
    );
  // One step open at a time, opened at no line.
  const openStepChange = (stepId: string | null) =>
    setView({ step: stepId, line: null, version: null });
  const artwork = order.artwork;
  const lineNames = new Map(order.lines.map((l) => [l.line_no, l.name.title]));
  // An order's command of the object contract, on the order or on one of its lines (enabled, or with why not).
  const commandOn = (lineRef: string | null, name: string) =>
    (lineRef === null
      ? order.commands
      : (order.lines.find((l) => l.line_ref === lineRef)?.commands ?? [])
    ).find((c) => c.name === name);
  const addKits = commandOn(null, ADD_KITS);
  const cancelOrder = commandOn(null, CANCEL_ORDER);
  const renderPanel = (step: RailStep) => {
    switch (step.id) {
      case "artwork":
        return artwork ? (
          <ArtworkAndProofs
            artwork={artwork}
            lineNames={lineNames}
            party={customerName}
            focusLineNo={focusLineNo}
            focusVersion={focusVersion}
            handlers={{
              busy,
              onOpenDialog: setArtworkDialog,
              onContact: order.actions.customer_contact
                ? () => setDialog({ kind: "contact_customer" })
                : undefined,
              onDownload: (url) => void downloadArtworkFile(url),
            }}
          />
        ) : null;
      case "payment":
        return (
          <PaymentStepPanel
            order={order}
            handlers={{
              busy,
              onChangeMethod: () =>
                setDialog({ kind: "change_payment_method" }),
              onCopyPayLink: (addedLineRef) =>
                void copyPayLink(order, addedLineRef),
              onViewProformaPdf: (proforma) => void viewDocumentPdf(proforma),
              onRecordPayment: (proforma) =>
                setDialog({
                  kind: "record_payment",
                  proformaNo: proforma.proforma_no,
                }),
              onMarkExcessRefunded: (proforma) =>
                void markExcessRefunded(order, proforma.proforma_no),
              onResend: (proforma) =>
                void resendEmail(order, proforma.proforma_no),
            }}
          />
        );
      case "order":
        return (
          <LinesStepPanel
            order={order}
            focusLineNo={focusLineNo}
            stepHref={stepHref}
            busy={busy}
            onCancelLine={(lineRef) =>
              setDialog({ kind: "cancel_line", lineRef })
            }
            onNameVersions={(lineRef) =>
              setDialog({ kind: "name_versions", lineRef })
            }
            onChangeDelivery={(lineRef) =>
              setDialog({ kind: "change_delivery", lineRef })
            }
            onRunCommand={runLineCommand}
          />
        );
      case "kits":
        return order.kits ? (
          <KitsStepPanel
            kits={order.kits}
            focusLineNo={focusLineNo}
            handlers={{
              busy,
              onDownloadManifest: (url) => void downloadKitManifest(url),
              recordDespatched: {
                canRecord: (lineRef) =>
                  commandOn(lineRef, RECORD_MADE_DESPATCHED)?.enabled === true,
                open: (lineRef, deliveryLineId) => {
                  const command = commandOn(lineRef, RECORD_MADE_DESPATCHED);
                  if (!command) return;
                  setRunning({
                    command,
                    context: `Order ${order.order_no} · ${order.title}`,
                    initialBody: { delivery_line_id: deliveryLineId },
                  });
                },
              },
              despatchTogether: {
                canDespatch: (lineRef) =>
                  commandOn(lineRef, DESPATCH_KIT_DROPS)?.enabled === true,
                open: (lineRef, dropNumbers) => {
                  const command = commandOn(lineRef, DESPATCH_KIT_DROPS);
                  if (!command) return;
                  setRunning({
                    command,
                    context: `Order ${order.order_no} · ${order.title}`,
                    // The drops ticked on the Kits tab; none starts from every drop still waiting.
                    ...(dropNumbers.length > 0
                      ? {
                          initialBody: {
                            drops: dropNumbers.map((drop) => ({ drop })),
                          },
                        }
                      : {}),
                  });
                },
              },
            }}
          />
        ) : null;
      case "release":
        return (
          <ReleaseStepPanel
            order={order}
            focusLineNo={focusLineNo}
            lineChips={(lineRef) => (
              <AlertChips
                variant="row"
                alerts={subjectAlerts.alerts.filter(
                  (a) => a.subject_ref === lineRef,
                )}
                onOpen={alertSheet.onOpen}
              />
            )}
            handlers={{
              busy,
              onRelease: (lineRef) =>
                setDialog({ kind: "release", lineRefs: [lineRef] }),
              onReleaseAll: () =>
                setDialog({
                  kind: "release",
                  lineRefs: linesReadyToRelease(order).map((l) => l.line_ref),
                }),
              onPlaceNow: (lineRef) => void placeNow(order, lineRef),
              onChangeSupplier: (lineRef) =>
                setDialog({
                  kind: "change_supplier",
                  lineRef,
                  returnToRelease: false,
                }),
              onCancelLine: (lineRef) =>
                setDialog({ kind: "cancel_line", lineRef }),
              onSetReview: (isOn) =>
                void setReleaseSwitch(order, {
                  kind: "set_review_before_release",
                  input: { review_before_release: isOn },
                }),
              onSetDeliverTogether: (isOn) =>
                void setReleaseSwitch(order, {
                  kind: "set_deliver_together",
                  input: { deliver_together: isOn },
                }),
            }}
          />
        );
      case "production":
        return <ProductionStepPanel order={order} focusLineNo={focusLineNo} />;
      case "invoice":
        return (
          <InvoiceStepPanel
            order={order}
            focusLineNo={focusLineNo}
            handlers={{
              busy,
              onViewPdf: (invoice) => void viewDocumentPdf(invoice),
              onSetVat: (line) => setDialog({ kind: "set_vat", line }),
            }}
          />
        );
      case "delivery":
        return (
          <DeliveryStepPanel
            order={order}
            focusLineNo={focusLineNo}
            focusVersion={focusVersion}
            busy={busy}
            onRequestRecord={setRecording}
            onRecordDespatched={(groupId) =>
              setDialog({ kind: "record_despatched", groupId })
            }
            onAgreeDeliveryPrice={(groupId) =>
              setDialog({ kind: "agree_delivery_price", groupId })
            }
            onChangeDelivery={(lineRef) =>
              setDialog({ kind: "change_delivery", lineRef })
            }
          />
        );
      default:
        // The Quote step links its quote; it has no panel.
        return null;
    }
  };
  const contacting =
    dialog?.kind === "contact"
      ? order.actions.contacts.find((c) => c.supplier_id === dialog.supplierId)
      : undefined;
  const lineOf = (lineRef: string) =>
    order.lines.find((l) => l.line_ref === lineRef);
  const cancelling =
    dialog?.kind === "cancel_line" ? lineOf(dialog.lineRef) : undefined;
  const naming =
    dialog?.kind === "name_versions" ? lineOf(dialog.lineRef) : undefined;
  const raisingJob =
    dialog?.kind === "record_tharstern_job"
      ? lineOf(dialog.lineRef)
      : undefined;
  const recordingDespatch =
    dialog?.kind === "record_despatched"
      ? order.delivery_groups.find((g) => g.id === dialog.groupId)
      : undefined;
  const agreeingDeliveryPrice =
    dialog?.kind === "agree_delivery_price"
      ? order.delivery_groups.find((g) => g.id === dialog.groupId)
      : undefined;
  const releasing =
    dialog?.kind === "release"
      ? dialog.lineRefs
          .map(lineOf)
          .filter((l): l is OrderView["lines"][number] => !!l?.release)
      : [];
  const changingSupplier =
    dialog?.kind === "change_supplier" ? lineOf(dialog.lineRef) : undefined;
  const changingDelivery =
    dialog?.kind === "change_delivery" ? lineOf(dialog.lineRef) : undefined;

  return (
    // Room at the foot so the floating Feedback button never covers a row's step.
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <ObjectHeader
        crumbs={[
          { label: "Customers", to: paths.customers() },
          {
            label: customerName,
            to: paths.customerDetail({ customerId: order.customer_code }),
          },
          {
            label: "Orders",
            to: paths.customerOrders({ customerId: order.customer_code }),
          },
          { label: order.order_no },
        ]}
        title={`Order ${order.order_no} · ${order.title}`}
        pills={
          <>
            <OrderStatusPill status={order.status} label={order.status_label} />
            <HealthPill health={order.health} label={order.health_label} />
          </>
        }
        counterpart={
          <Link
            to={paths.customerDetail({ customerId: order.customer_code })}
            className="hover:underline"
          >
            {customerName}
          </Link>
        }
        value={formatQuotedPrice(order.value_pence)}
        valueLabel="order total, ex VAT"
        facts={[
          {
            label: lines.length === 1 ? "Line" : "Lines",
            value:
              lines.length === 1 ? lines[0]!.name.full : String(lines.length),
          },
          ...(order.required_on
            ? [
                {
                  label: "Needed by",
                  value: formatDateOrDash(order.required_on),
                },
              ]
            : []),
          ...(order.customer_order_number_label
            ? [
                {
                  label: "Customer PO",
                  value: (
                    <span className="inline-flex flex-wrap items-baseline gap-x-2">
                      <span>{order.customer_order_number_label}</span>
                      {order.record_customer_order_number_url && (
                        <button
                          type="button"
                          onClick={() =>
                            setDialog({ kind: "record_po_number" })
                          }
                          className="text-sm underline underline-offset-2"
                        >
                          Add PO number…
                        </button>
                      )}
                    </span>
                  ),
                },
              ]
            : []),
          // Orders' approve-by sentence: what the customer is told too.
          ...(order.approve_by
            ? [
                {
                  label: "Approve by",
                  value: (
                    <span
                      className={cn(
                        order.approve_by.tier &&
                          APPROVE_BY_TONE[order.approve_by.tier],
                      )}
                    >
                      {order.approve_by.sentence}
                    </span>
                  ),
                },
              ]
            : []),
          ...(order.project
            ? [
                {
                  label: "Project",
                  value: (
                    <ProjectChip
                      project={order.project}
                      currentOrderNo={order.order_no}
                    />
                  ),
                },
              ]
            : []),
        ]}
        disclosure={
          <button
            type="button"
            aria-expanded={isDetailsOpen}
            aria-controls="order-details"
            onClick={() =>
              setView(
                { details: isDetailsOpen ? null : "1" },
                { replace: true },
              )
            }
            className="inline-flex items-center gap-0.5 rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            Details
            <ChevronDown
              aria-hidden
              className={cn(
                "size-4 transition-transform",
                isDetailsOpen && "rotate-180",
              )}
            />
          </button>
        }
        actions={
          <>
            {addKits && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A disabled button takes no pointer events: the tooltip hangs off a wrapper. */}
                  <span tabIndex={addKits.enabled ? undefined : 0}>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || !addKits.enabled}
                      onClick={() =>
                        setRunning({
                          command: addKits,
                          context: `Order ${order.order_no} · ${order.title}`,
                          opensKits: true,
                        })
                      }
                    >
                      Add kits…
                    </Button>
                  </span>
                </TooltipTrigger>
                {!addKits.enabled && addKits.reason && (
                  <TooltipContent>{addKits.reason}</TooltipContent>
                )}
              </Tooltip>
            )}
            {cancelOrder && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A disabled button takes no pointer events: the tooltip hangs off a wrapper. */}
                  <span tabIndex={cancelOrder.enabled ? undefined : 0}>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      disabled={busy || !cancelOrder.enabled}
                      onClick={() =>
                        setRunning({
                          command: cancelOrder,
                          context: `Order ${order.order_no} · ${order.title}`,
                        })
                      }
                    >
                      Cancel order…
                    </Button>
                  </span>
                </TooltipTrigger>
                {!cancelOrder.enabled && cancelOrder.reason && (
                  <TooltipContent>{cancelOrder.reason}</TooltipContent>
                )}
              </Tooltip>
            )}
            {order.add_extra && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDialog({ kind: "add_extra" })}
              >
                <Plus aria-hidden className="size-4" />
                Add extra
              </Button>
            )}
            {/* Not modal: its Activity item opens the side sheet, which takes focus. */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" aria-label="More actions">
                  <MoreHorizontal aria-hidden className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {order.add_to_project_url && (
                  <DropdownMenuItem
                    onSelect={() => setDialog({ kind: "add_to_project" })}
                  >
                    Add to project…
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setView({ activity: "1" })}>
                  Activity…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      >
        {isDetailsOpen && (
          <div
            id="order-details"
            data-section-id="order.details"
            className="space-y-3 border-t border-border pt-3"
          >
            <OrderDetailsPanel
              order={order}
              onSetContact={() => setDialog({ kind: "set_contact" })}
            />
            <PlannedOrderRowGroups where="details" />
          </div>
        )}
        <div data-section-id="order.action_box">
          <OrderActionBox
            orderNo={order.order_no}
            box={order.actions}
            isExpanded={isBoxOpen}
            onExpandedChange={(isExpanded) =>
              setView({ now: isExpanded ? "1" : null }, { replace: true })
            }
            handlers={{
              busy,
              onRecordDeliveryLine: (id) =>
                setRecording(findDeliveryLine(order, id)),
              onContact: (supplierId) =>
                setDialog({ kind: "contact", supplierId }),
              onContactCustomer: () => setDialog({ kind: "contact_customer" }),
              onSetContact: () => setDialog({ kind: "set_contact" }),
              onChangePaymentMethod: () =>
                setDialog({ kind: "change_payment_method" }),
              onRecordTharsternJob: (lineRef) =>
                setDialog({ kind: "record_tharstern_job", lineRef }),
              onRelease: (lineRef) =>
                setDialog({ kind: "release", lineRefs: [lineRef] }),
              onPlaceNow: (lineRef) => void placeNow(order, lineRef),
              onChangeSupplier: (lineRef) =>
                setDialog({
                  kind: "change_supplier",
                  lineRef,
                  returnToRelease: false,
                }),
              onPlaceWithSupplier: (purchaseOrderNo) =>
                navigate(paths.placement({ purchaseOrderNo })),
              onArtworkAction: (button) =>
                setArtworkDialog(artworkRequestOf(button)),
            }}
            planned={
              hasPreviews ? (
                <PlannedOrderRowGroups where="action_box" />
              ) : undefined
            }
          />
        </div>
        {notice && (
          <p role="status" className="text-sm text-muted-foreground">
            {notice}
          </p>
        )}
        <div className="border-t border-border pt-3">
          <LifecycleRail
            rail={rail}
            ariaLabel={`Where order ${order.order_no} is`}
            renderPanel={renderPanel}
            openStepId={isRailStep(openStep) ? openStep : null}
            onOpenStepChange={openStepChange}
          />
        </div>
      </ObjectHeader>

      <SubjectAlerts
        alerts={subjectAlerts.alerts}
        subjectRef={order.order_no}
        onChanged={subjectAlerts.reload}
        {...alertSheet}
      />
      {running && (
        <CommandShell
          key={running.command.href}
          endpoint={endpointOf(
            running.command,
            objectIndex.commandOf(running.command.name),
          )}
          context={running.context}
          widgets={COMMAND_WIDGETS}
          {...(running.initialBody ? { initialBody: running.initialBody } : {})}
          onClose={() => setRunning(null)}
          onDone={(result) => {
            const opensKits = running.opensKits;
            setRunning(null);
            toast.success(result.summary);
            rereadOrder();
            if (opensKits) setView({ step: "kits", line: null, version: null });
          }}
        />
      )}

      <Sheet
        open={isActivityOpen}
        onOpenChange={(open) => {
          if (!open) setView({ activity: null }, { replace: true });
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Activity</SheetTitle>
            <SheetDescription>
              Everything that has happened on order {order.order_no}, newest
              first.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6" data-section-id="order.activity">
            <ActivityTimeline entries={order.activity} />
          </div>
        </SheetContent>
      </Sheet>

      {artwork && (
        <ArtworkDialogHost
          artwork={artwork}
          lineNames={lineNames}
          request={artworkDialog}
          party={customerName}
          busy={busy}
          onClose={() => setArtworkDialog(null)}
          onSubmit={(command) => applyArtwork(order, command)}
        />
      )}
      {recording && (
        <LineCompletionDialog
          key={recording.id}
          line={recording}
          busy={busy}
          onClose={() => setRecording(null)}
          onRecord={(completion) =>
            apply(order, recordCommand(recording, completion))
          }
        />
      )}
      {dialog?.kind === "add_extra" && order.add_extra && (
        <AddExtraDialog
          orderNo={order.order_no}
          addExtra={order.add_extra}
          busy={busy}
          onClose={() => setDialog(null)}
          onAdd={(input) => apply(order, { kind: "add_extra", input })}
        />
      )}
      {dialog?.kind === "add_to_project" && (
        <AddToProjectDialog
          orderNo={order.order_no}
          projects={order.payer_projects}
          suggestion={order.project_title_suggestion}
          busy={busy}
          onClose={() => setDialog(null)}
          onAdd={(input) => apply(order, { kind: "add_to_project", input })}
        />
      )}
      {dialog?.kind === "contact_customer" &&
        order.actions.customer_contact && (
          <ContactCustomerDialog
            party={order.actions.customer_contact}
            busy={busy}
            onClose={() => setDialog(null)}
            onLog={(input) => apply(order, { kind: "log_contact", input })}
          />
        )}
      {dialog?.kind === "set_contact" && order.set_contact_url && (
        <SetContactDialog
          orderNo={order.order_no}
          people={order.contact_options}
          current={order.contact}
          busy={busy}
          onClose={() => setDialog(null)}
          onSet={(input) => apply(order, { kind: "set_contact", input })}
          lookup={
            order.customer_id && isFeatureEnabled("v1_completion")
              ? {
                  customerId: order.customer_id,
                  customerName: order.customer_name ?? order.customer_code,
                  suggest: (query) =>
                    adapter.suggestPortalContacts(order.customer_id!, query),
                  findOrCreate: (choice) =>
                    adapter.findOrCreatePortalPerson(
                      order.customer_id!,
                      choice,
                    ),
                }
              : null
          }
        />
      )}
      {dialog?.kind === "change_payment_method" && order.payment && (
        <ChangePaymentMethodDialog
          orderNo={order.order_no}
          current={order.payment.method_label}
          choices={order.payment.change_method_choices}
          busy={busy}
          onClose={() => setDialog(null)}
          onChange={(input) =>
            apply(order, { kind: "change_payment_method", input })
          }
        />
      )}
      {dialog?.kind === "record_po_number" && (
        <RecordPoNumberDialog
          customerName={customerName}
          poFormatHint={poFormatHint(order)}
          busy={busy}
          onClose={() => setDialog(null)}
          onRecord={(input) =>
            apply(order, { kind: "record_customer_order_number", input })
          }
        />
      )}
      {dialog?.kind === "set_vat" && order.billing && (
        <SetVatDialog
          lineNo={dialog.line.line_no}
          options={order.billing.vat_treatment_options}
          busy={busy}
          onClose={() => setDialog(null)}
          onSet={(input) =>
            apply(order, {
              kind: "set_vat_treatment",
              invoiceLineId: dialog.line.invoice_line_id,
              input,
            })
          }
        />
      )}
      {dialog?.kind === "record_payment" &&
        (() => {
          const paying = order.billing?.proformas.find(
            (p) => p.proforma_no === dialog.proformaNo,
          );
          return paying ? (
            <RecordPaymentDialog
              proforma={paying}
              busy={busy}
              onClose={() => setDialog(null)}
              onRecord={async (input) => {
                const refused = await apply(order, {
                  kind: "record_proforma_payment",
                  proformaNo: paying.proforma_no,
                  input,
                });
                if (!refused)
                  setNotice(`Recorded a payment on ${paying.proforma_no}.`);
                return refused;
              }}
            />
          ) : null;
        })()}
      {raisingJob && (
        <RecordTharsternJobDialog
          key={raisingJob.line_ref}
          lineLabel={`${raisingJob.line_no} ${raisingJob.name.title}`}
          currentJobNo={raisingJob.tharstern_job_no ?? null}
          busy={busy}
          onClose={() => setDialog(null)}
          onRecord={async (input) => {
            const refused = await apply(order, {
              kind: "record_tharstern_job",
              lineRef: raisingJob.line_ref,
              input,
            });
            if (!refused)
              setNotice(
                `Recorded Tharstern job ${input.tharstern_job_no} for ${raisingJob.line_no}.`,
              );
            return refused;
          }}
        />
      )}
      {agreeingDeliveryPrice?.charge?.agree_price_url && (
        <AgreeDeliveryPriceDialog
          key={agreeingDeliveryPrice.id}
          group={agreeingDeliveryPrice}
          busy={busy}
          onClose={() => setDialog(null)}
          onAgree={async (input) => {
            const refused = await apply(order, {
              kind: "agree_delivery_price",
              groupId: agreeingDeliveryPrice.id,
              input,
            });
            if (!refused) setNotice("Delivery price agreed.");
            return refused;
          }}
        />
      )}
      {recordingDespatch?.record_despatched_together && (
        <RecordDespatchedTogetherDialog
          key={recordingDespatch.id}
          group={recordingDespatch}
          together={recordingDespatch.record_despatched_together}
          busy={busy}
          onClose={() => setDialog(null)}
          onRecord={async (input) => {
            const refused = await apply(order, {
              kind: "record_despatched_together",
              groupId: recordingDespatch.id,
              input,
            });
            if (!refused)
              setNotice(despatchedTogetherToast(order.order_no, input));
            return refused;
          }}
          onPreviewBooking={(input) =>
            adapter.previewCourierBooking(order, {
              kind: "book_courier_despatch",
              groupId: recordingDespatch.id,
              input,
            })
          }
          onBook={async (input) => {
            // Book, show the re-read order, then open the label the booking handed back (fetched with the
            // person's sign-in, never a plain anchor); a label that fails to open leaves the booking made.
            setBusy(true);
            try {
              const booked = await adapter.bookCourierDespatch(order, {
                kind: "book_courier_despatch",
                groupId: recordingDespatch.id,
                input,
              });
              setState({ kind: "loaded", order: booked.order });
              setNotice(booked.summary);
              try {
                const { blob } = await adapter.downloadCourierLabel(
                  booked.label_href,
                );
                capabilities.openBlobInNewTab(blob);
              } catch {
                setNotice(
                  `${booked.summary} The label could not be opened: print it from the despatch's label link.`,
                );
              }
              return null;
            } catch (err) {
              return orderStepFailure(err);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
      {recordingDespatch &&
        (recordingDespatch.record_made_despatched ?? []).length > 0 && (
          <RecordMadeDespatchedDialog
            key={recordingDespatch.id}
            group={recordingDespatch}
            lines={recordingDespatch.record_made_despatched ?? []}
            busy={busy}
            onClose={() => setDialog(null)}
            onRecord={async (action, input) => {
              const refused = await apply(order, {
                kind: "record_made_despatched",
                lineRef: action.line_ref,
                deliveryLineId: action.delivery_line_id,
                input,
              });
              if (!refused) setNotice("Despatch recorded.");
              return refused;
            }}
          />
        )}
      {releasing.length > 0 && (
        <ReleaseDialog
          key={releasing.map((l) => l.line_ref).join(",")}
          orderNo={order.order_no}
          lines={releasing}
          busy={busy}
          onClose={() => setDialog(null)}
          onRelease={async (lines, placement) => {
            const refused = await apply(order, {
              kind: "release_lines",
              input: {
                lines: lines.map((l) => ({
                  line_ref: l.lineRef,
                  is_confirmed: l.isConfirmed,
                })),
                placement,
              },
            });
            if (!refused) {
              const names = lines
                .map((l) => lineOf(l.lineRef)?.line_no)
                .join(", ");
              setNotice(
                placement === "hold_to_place_together"
                  ? `Released ${names}: held to place together.`
                  : `Released ${names}.`,
              );
            }
            return refused;
          }}
          onHold={async (lineRef, reason) => {
            const refused = await apply(order, {
              kind: "hold_release",
              lineRef,
              input: { reason },
            });
            if (!refused) setNotice(`Held ${lineOf(lineRef)?.line_no}.`);
            return refused;
          }}
          onChangeSupplier={(lineRef) =>
            setDialog({
              kind: "change_supplier",
              lineRef,
              returnToRelease: true,
            })
          }
          onDownloadPrintReady={(file) => void downloadFileAt(file)}
        />
      )}
      {changingSupplier?.release?.change_supplier &&
        dialog?.kind === "change_supplier" && (
          <ChangeSupplierDialog
            key={changingSupplier.line_ref}
            title={`Change supplier · ${changingSupplier.line_no} ${changingSupplier.name.title}`}
            view={changingSupplier.release.change_supplier}
            busy={busy}
            onClose={() =>
              setDialog(
                dialog.returnToRelease
                  ? { kind: "release", lineRefs: [changingSupplier.line_ref] }
                  : null,
              )
            }
            onConfirm={async (input) => {
              const refused = await apply(order, {
                kind: "change_supplier",
                lineRef: changingSupplier.line_ref,
                input,
              });
              if (!refused)
                setNotice(
                  `Changed the supplier of ${changingSupplier.line_no}.`,
                );
              return refused;
            }}
          />
        )}
      {cancelling?.cancel_rest && (
        <CancelRestDialog
          rest={cancelling.cancel_rest}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={async (input) => {
            const refused = await apply(order, {
              kind: "cancel_line",
              lineRef: cancelling.line_ref,
              input,
            });
            if (!refused)
              setNotice(`Cancelled the rest of ${cancelling.line_no}.`);
            return refused;
          }}
        />
      )}
      {changingDelivery?.change_delivery && (
        <ChangeDeliveryDialog
          key={changingDelivery.line_ref}
          order={order}
          line={changingDelivery}
          view={changingDelivery.change_delivery}
          onClose={() => setDialog(null)}
          onSaved={(next) => {
            setState({ kind: "loaded", order: next });
            setDialog(null);
            setNotice("Delivery changed.");
          }}
        />
      )}
      {cancelling && !cancelling.cancel_rest && (
        <CancelLineDialog
          title={`Cancel line ${cancelling.line_no}`}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            const refused = await apply(order, {
              kind: "cancel_line",
              lineRef: cancelling.line_ref,
              input: { reason },
            });
            if (!refused) setNotice(`Cancelled ${cancelling.line_no}.`);
            return refused;
          }}
        />
      )}
      {naming && (
        <NameVersionsDialog
          key={naming.line_ref}
          title={`Name versions · ${naming.line_ref} ${naming.name.title}`}
          description="Each version is its own artwork. Its label and name can change; the number of versions and their quantities stay as ordered."
          versions={naming.versions.map((v, i) => ({ ...v, position: i + 1 }))}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={async (versions) => {
            const refused = await apply(order, {
              kind: "set_version_labels",
              lineRef: naming.line_ref,
              input: { versions },
            });
            if (!refused) setNotice(`Named ${naming.line_no}'s versions.`);
            return refused;
          }}
        />
      )}
      {contacting && (
        <ContactDialog
          key={contacting.supplier_id}
          party={contacting}
          today={londonTodayIso()}
          busy={busy}
          onClose={() => setDialog(null)}
          onLog={(input) =>
            apply(order, {
              kind: "log_supplier_call",
              supplierNo: contacting.supplier_no ?? "",
              input,
            })
          }
        />
      )}
    </div>
  );
}
